import React from 'react';
import axios from 'axios';
import {
  Rocket,
  School,
  Radar,
  ArrowUpRight,
  ArrowRight,
} from 'lucide-react';
import { Reveal } from '../pop/motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OfferingsSection — McKinsey / BCG editorial standard.
 *
 * Functionality preserved:
 *   · Same 3 offerings with identical IDs, hrefs, internal-vs-external behaviour.
 *   · trackOfferingClick fires on click + auxClick (fire-and-forget).
 *   · data-testids: offerings-section, offerings-new-badge, offering-card-{id}.
 */

const trackOfferingClick = (offeringId) => {
  // Fire-and-forget analytics beacon. Never block navigation.
  try {
    const payload = {
      offering_id: offeringId,
      source: 'homepage-offerings',
      referrer: document.referrer || '',
      user_agent: navigator.userAgent || '',
      path: window.location.pathname || '',
    };
    axios.post(`${API}/offerings/track-click`, payload).catch(() => {});
  } catch (_e) {
    // swallow — analytics should never break UX
  }
};

const OFFERINGS = [
  {
    n: '01',
    id: 'company-intelligence',
    tag: 'Assess',
    isNew: true,
    name: 'Company Intelligence',
    tagline: 'Board-grade company analysis, agentically assembled',
    description:
      'A 15-agent strategic-intelligence system that converts annual reports, filings and fragmented data into an evidence-gated Enterprise Value Scorecard — with peer benchmarking, quantified value pools and a 36-month board roadmap.',
    href: '/company-intelligence',
    internal: true,
    Icon: Radar,
    proofs: ['15 AI agents', 'Leadership Quadrant', 'Board-pack export'],
    cta: 'Explore the Intelligence Suite',
  },
  {
    n: '02',
    id: 'project-pulse-ai-2',
    tag: 'Build',
    name: 'Vantage',
    tagline: 'Project resource intelligence',
    description:
      'A single, authoritative view of every project resource. Vantage auto-identifies people, skills and assets, applies rich tags, and maps them to the right projects — turning fragmented rosters into a decision-ready workforce graph.',
    href: 'https://quadrant-lens.preview.emergentagent.com/',
    internal: false,
    Icon: Rocket,
    proofs: ['Resource identification', 'Smart tagging', 'Project mapping'],
    cta: 'Launch Vantage',
  },
  {
    n: '03',
    id: 'pop-portal',
    tag: 'Connect',
    name: 'SAMANVAI PoP Exchange',
    tagline: 'Industry × academia confluence',
    description:
      'A UGC-aligned ecosystem connecting universities, industry experts, faculty and students on one verified network — for building, discovering, engaging and growing every Professor of Practice stakeholder.',
    href: '/pop',
    internal: true,
    Icon: School,
    proofs: ['Multi-stakeholder', 'Verified experts', 'UGC-aligned'],
    cta: 'Enter the Portal',
  },
];

const OfferingsSection = () => {
  return (
    <section
      id="offerings"
      data-testid="offerings-section"
      className="relative py-24 sm:py-32 bg-[#0a1220] overflow-hidden"
    >
      {/* Background — restrained editorial texture */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />
        <div className="absolute -bottom-32 right-1/4 w-[600px] h-[600px] rounded-full bg-amber-400/[0.05] blur-3xl" />
        <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.05] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* --------- Editorial masthead --------- */}
        <Reveal className="max-w-3xl mb-16 sm:mb-20">
          <div
            data-testid="offerings-new-badge"
            className="text-[10px] tracking-[0.32em] font-bold uppercase text-amber-300 mb-6"
          >
            Part of the SAMANVAI Suite &nbsp;·&nbsp; Three adjacent platforms
          </div>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight mb-6">
            Beyond diagnostics.
            <br />
            <span className="italic text-amber-300">
              The platforms that move the needle.
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-white/70 leading-relaxed font-light max-w-2xl">
            Insight without follow-through is a slide deck. SAMANVAI ships three
            adjacent platforms — to
            <span className="text-white font-normal"> assess</span> what boards
            actually reward,
            <span className="text-white font-normal"> build</span> the resource
            intelligence your PMO wishes it had, and
            <span className="text-white font-normal"> connect</span> your
            institution to the industry practitioners the market rewards.
          </p>
        </Reveal>

        {/* --------- Editorial offerings grid --------- */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 sm:gap-y-16">
          {OFFERINGS.map((o, idx) => {
            const isInternal = !!o.internal;
            const anchorProps = isInternal
              ? { href: o.href }
              : { href: o.href, target: '_blank', rel: 'noopener noreferrer' };
            const Icon = o.Icon;

            return (
              <Reveal key={o.id} delay={idx * 80}>
                <a
                  {...anchorProps}
                  onClick={() => trackOfferingClick(o.id)}
                  onAuxClick={() => trackOfferingClick(o.id)}
                  data-testid={`offering-card-${o.id}`}
                  className="group block border-t border-white/15 pt-6 hover:border-amber-300/70 transition-colors"
                >
                  {/* Top row: numeral + icon */}
                  <div className="flex items-start justify-between mb-5">
                    <div className="font-serif text-white/25 text-4xl leading-none tabular-nums group-hover:text-amber-300/50 transition-colors">
                      {o.n}
                    </div>
                    <Icon
                      className="w-4 h-4 text-white/35 group-hover:text-amber-300 transition-colors"
                      strokeWidth={1.75}
                    />
                  </div>

                  {/* Tag / kicker */}
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-amber-300/80 mb-2">
                    {o.tag}
                    {o.isNew && (
                      <span className="ml-2 text-[9px] tracking-[0.18em] font-bold text-teal-300 border border-teal-400/40 rounded-full px-2 py-0.5">
                        NEW
                      </span>
                    )}
                    {!isInternal && (
                      <span className="ml-2 text-white/35 font-normal tracking-[0.2em]">
                        · External
                      </span>
                    )}
                  </div>

                  {/* Name + tagline */}
                  <h3 className="font-serif text-white text-2xl sm:text-[28px] leading-tight mb-1 group-hover:text-amber-100 transition-colors">
                    {o.name}
                  </h3>
                  <div className="text-[13px] text-white/50 italic mb-4">
                    {o.tagline}
                  </div>

                  {/* Editorial description */}
                  <p className="text-[15px] text-white/65 leading-relaxed mb-6">
                    {o.description}
                  </p>

                  {/* Proof strip — restrained, editorial dot list */}
                  <ul className="flex flex-wrap gap-x-4 gap-y-1.5 mb-7">
                    {o.proofs.map((p) => (
                      <li
                        key={p}
                        className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase font-semibold text-white/55"
                      >
                        <span className="w-1 h-1 rounded-full bg-amber-300/70" />
                        {p}
                      </li>
                    ))}
                  </ul>

                  {/* CTA row */}
                  <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-white/60 group-hover:text-amber-300 transition-colors">
                    {o.cta}
                    {isInternal ? (
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    ) : (
                      <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                    )}
                  </div>
                </a>
              </Reveal>
            );
          })}
        </div>

        {/* --------- Editorial closing note --------- */}
        <Reveal delay={240}>
          <div className="mt-24 border-t border-white/10 pt-10 flex flex-col md:flex-row md:items-end gap-6 md:gap-12">
            <div className="max-w-md">
              <div className="text-[10px] tracking-[0.32em] font-bold uppercase text-white/45 mb-3">
                One suite, three surfaces
              </div>
              <p className="font-serif text-white text-2xl sm:text-3xl leading-snug">
                Each platform is standalone.
                <span className="italic text-amber-300"> Together,</span> they
                convert a maturity diagnostic into an intelligence, delivery
                and academia flywheel — measured in outcomes, not
                activities.
              </p>
            </div>
            <div className="md:ml-auto text-[11px] tracking-[0.22em] uppercase font-semibold text-white/40">
              External platforms open in a new tab
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default OfferingsSection;
